/**
 * Pure resolver for the hero card morph SOURCE geometry.
 *
 * The clicked hero card is a {@link Cbutton}: the element the click handler
 * receives (`event.currentTarget`) is the `role="button"` HIT WRAPPER, not the
 * rounded visual surface. Cbutton renders that wrapper around a single
 * `Ccontainer` surface which carries the real corner radius (`model.radius`, 48px
 * for a Cteamcard); the wrapper's own computed radius is `0`. `parseFloat("0px")`
 * is a finite `0`, so reading the radius off the wrapper silently starts the morph
 * from a SQUARE collapsed corner instead of the card's visible rounded corner.
 *
 * The caller reads the visual surface geometry off the DOM (the wrapper's content
 * root) and hands it here. This resolver owns the decision:
 *  - the visual surface is authoritative for BOTH rect and radius — a genuinely
 *    square surface (radius 0) is preserved, never "corrected" to the fallback;
 *  - the wrapper's own radius is never authoritative;
 *  - only when no surface can be resolved do we fall back to the wrapper rect and
 *    the known Cteamcard radius.
 */

/** The Cteamcard surface radius — used only when no visual surface is resolvable. */
export const HERO_CARD_RADIUS_FALLBACK = 48;

/** Visual-surface geometry the caller read from the DOM (rect kept opaque so this
 *  stays a pure, DOM-free decision that unit tests can exercise with sentinels). */
export interface HeroCardSurfaceGeometry<TRect> {
  /** The visual surface's own rect — what actually morphs. */
  rect: TRect;
  /** The visual surface's computed corner radius; a genuine `0` is authoritative. */
  radius: number;
}

export interface HeroCardMorphGeometry<TRect> {
  rect: TRect;
  radius: number;
}

/**
 * Resolve the collapsed rect + radius the panel morph starts from.
 *
 * @param surface     visual surface geometry, or `null` when none was resolvable
 * @param wrapperRect the clicked hit-wrapper rect — the fallback rect only
 * @param fallbackRadius radius used when no surface resolves (Cteamcard = 48)
 */
export function resolveHeroCardMorphGeometry<TRect>({
  surface,
  wrapperRect,
  fallbackRadius = HERO_CARD_RADIUS_FALLBACK,
}: {
  surface: HeroCardSurfaceGeometry<TRect> | null;
  wrapperRect: TRect;
  fallbackRadius?: number;
}): HeroCardMorphGeometry<TRect> {
  if (surface == null) {
    return { rect: wrapperRect, radius: fallbackRadius };
  }
  return { rect: surface.rect, radius: surface.radius };
}
